import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import '@/api';
import '@/i18n';
import { router } from './routes';
import reportWebVitals from './reportWebVitals';
import * as TanStackQueryProvider from '@/shared/integrations/tanstack-query/root-provider';
import { validateCriticalConfig } from '@/shared/config/validator';
import { CriticalError } from '@/shared/components/CriticalError';
import { rememberComponentStack } from '@/shared/utils/caughtComponentStack';
import { AxiosInterceptor } from '@/shared/components/AxiosInterceptor';
import './styles.css';
import { canonicalPortalLocation } from '@/features/Auth/portalLocation';
import { config } from '@/shared/config/env';

const rootElement = document.getElementById('app');
const missingKeys = validateCriticalConfig();
const canonical = missingKeys.length
  ? null
  : canonicalPortalLocation(
      new URL(window.location.href),
      config.app.clientPortalUrl,
    );
if (canonical) window.location.replace(canonical);

if (!canonical && rootElement && !rootElement.innerHTML) {
  // React is the only thing that knows WHICH component threw: an error
  // boundary's fallback is handed the error alone (TanStack's drops the stack
  // its types promise), so a render fault could say nothing about where it came
  // from. Stash it here and the fallback can name the component.
  const root = ReactDOM.createRoot(rootElement, {
    onCaughtError: (error, errorInfo) =>
      rememberComponentStack(error, errorInfo.componentStack),
  });
  if (missingKeys.length > 0) {
    root.render(
      <StrictMode>
        <CriticalError
          title="Configuration Error"
          message="We encountered an issue with the application configuration. Please contact support."
          code="ERR_SYS_001"
        />
      </StrictMode>,
    );
  } else {
    root.render(
      <StrictMode>
        <TanStackQueryProvider.Provider>
          <AxiosInterceptor>
            <RouterProvider router={router} />
          </AxiosInterceptor>
        </TanStackQueryProvider.Provider>
      </StrictMode>,
    );
  }
}

reportWebVitals();
